import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ChevronRight, Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { whatsappUrl } from '@/lib/business'
import { BrandLogo } from '@/components/generals/brand-logo'
import { WhatsAppIcon } from '@/components/generals/icons'
import { contact, navLinks } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const [isOpen, setIsOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!isOpen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [isOpen])

    const closeMenu = () => setIsOpen(false)

    return (
        <header
            className={cn(
                'sticky top-0 z-40 border-b transition-[background-color,box-shadow,border-color] duration-300',
                isScrolled || isOpen
                    ? 'border-gold/20 bg-plum-deep/95 shadow-lg shadow-black/25 backdrop-blur-md'
                    : 'border-transparent bg-plum-deep'
            )}
        >
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8 lg:h-20'>
                <BrandLogo onClick={closeMenu} />

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className='rounded-full px-3.5 py-2 text-sm font-medium text-ivory/80 transition-colors hover:bg-white/5 hover:text-gold-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <a
                        href={contact.phoneHref}
                        className='inline-flex h-10 items-center gap-2 rounded-full border border-gold/40 px-3 text-sm font-medium text-gold-light transition-colors hover:border-gold hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:px-4'
                        aria-label={`Call Anushka Beauty Parlour on ${contact.phoneDisplay}`}
                    >
                        <Phone className='size-4' aria-hidden='true' />
                        <span className='hidden xl:inline'>{contact.phoneDisplay}</span>
                    </a>

                    <a
                        href={whatsappUrl()}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='gold-gradient hidden h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold text-plum-deep shadow-md shadow-gold/20 transition-[filter,transform] hover:brightness-110 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:inline-flex'
                    >
                        <WhatsAppIcon className='size-4' />
                        Book Now
                    </a>

                    <button
                        type='button'
                        onClick={() => setIsOpen(open => !open)}
                        aria-expanded={isOpen}
                        aria-controls='mobile-menu'
                        aria-label={isOpen ? 'Close menu' : 'Open menu'}
                        className='inline-flex size-10 items-center justify-center rounded-full border border-gold/40 text-gold-light transition-colors hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold lg:hidden'
                    >
                        {isOpen ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            {isOpen && (
                <div
                    id='mobile-menu'
                    className='animate-in fade-in slide-in-from-top-2 border-t border-gold/15 bg-plum-deep duration-200 lg:hidden'
                >
                    <nav aria-label='Mobile' className='mx-auto max-w-7xl px-5 pb-6 pt-3 sm:px-8'>
                        <ul className='divide-y divide-white/5'>
                            {navLinks.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        onClick={closeMenu}
                                        className='flex items-center justify-between rounded-lg px-2 py-3.5 text-base font-medium text-ivory/90 transition-colors hover:text-gold-light focus-visible:outline-2 focus-visible:outline-gold'
                                    >
                                        {link.label}
                                        <ChevronRight className='size-4 text-gold/70' aria-hidden='true' />
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <div className='mt-4 grid grid-cols-2 gap-3'>
                            <a
                                href={contact.phoneHref}
                                className='inline-flex h-12 items-center justify-center gap-2 rounded-full border border-gold/50 text-sm font-semibold text-gold-light'
                            >
                                <Phone className='size-4' aria-hidden='true' />
                                Call Now
                            </a>

                            <a
                                href={whatsappUrl()}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='gold-gradient inline-flex h-12 items-center justify-center gap-2 rounded-full text-sm font-semibold text-plum-deep'
                            >
                                <WhatsAppIcon className='size-4' />
                                WhatsApp
                            </a>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    )
}
